import { graphqlClient } from "@/apollo";
import {
  DeletePlannerTemplateDocument,
  GetPlannerCategoriesDocument,
  GetPlannerTemplatesDocument,
  SavePlannerCategoryDocument,
  SavePlannerTemplateDocument,
  type PlannerCategoryInput,
  GetPlannerFiltersDocument,
  type PlannerFilterInput,
  SavePlannerFilterDocument,
  DeletePlannerFilterDocument,
  type PlannerTemplateInput,
} from "@/graphql/generated-types";
import SentryGraphQLError from "@/helpers/exceptions/SentryGraphQLError";
import apiclient from "@/services/apiclient";
import { useAuthStore } from "@/stores/auth.module";
import type { NewFilterTypes } from "@/types";

const getFilters = async () => {
  const response = await apiclient.get("planner/get-filters");
  return response.data;
};

const getFiltersViaGQL = async () => {
  const response = await graphqlClient.query({
    query: GetPlannerFiltersDocument,
    fetchPolicy: "no-cache",
  });

  if (response.data.planner?.getFilters) {
    return response.data.planner?.getFilters as NewFilterTypes[];
  }

  throw new SentryGraphQLError(`api.planner.getFiltersNews`, response);
};

const getCategories = async () => {
  const response = await graphqlClient.query({
    query: GetPlannerCategoriesDocument,
    fetchPolicy: "no-cache",
  });

  if (response.data.planner?.getCategories) {
    return response.data.planner?.getCategories;
  }

  throw new SentryGraphQLError(`api.planner.getCategories`, response);
};

const saveCategory = async (category: PlannerCategoryInput) => {
  const authStore = useAuthStore();
  category.institution = authStore.institutionId;

  const response = await graphqlClient.query({
    query: SavePlannerCategoryDocument,
    variables: {
      category,
    },
    fetchPolicy: "no-cache",
  });

  if (response.data.planner?.saveCategory?.success) {
    return response.data.planner.saveCategory;
  }

  throw new SentryGraphQLError(`api.planner.saveCategory`, response);
};

const saveFilter = async (filter: PlannerFilterInput) => {
  const authStore = useAuthStore();
  filter.institution = authStore.institutionId;

  const response = await graphqlClient.query({
    query: SavePlannerFilterDocument,
    variables: {
      filter,
    },
    fetchPolicy: "no-cache",
  });

  if (response.data.planner?.saveFilter?.success) {
    return response.data.planner.saveFilter;
  }

  throw new SentryGraphQLError(`api.planner.saveFilter`, response);
};

const deleteFilter = async (id: string) => {
  const response = await graphqlClient.query({
    query: DeletePlannerFilterDocument,
    variables: {
      id,
    },
    fetchPolicy: "no-cache",
  });

  if (response.data.planner?.deleteFilter?.success) {
    return response.data.planner.deleteFilter;
  }

  throw new SentryGraphQLError(`api.planner.deleteFilter`, response);
};

const getPlannerTemplates = async () => {
  const response = await graphqlClient.query({
    query: GetPlannerTemplatesDocument,
    fetchPolicy: "no-cache",
  });

  if (response.data.plannerTemplates) {
    return response.data.plannerTemplates;
  }

  throw new SentryGraphQLError(`api.planner.getPlannerTemplates`, response);
};

const deletePlannerTemplates = async (id: string) => {
  const response = await graphqlClient.query({
    query: DeletePlannerTemplateDocument,
    variables: {
      id,
    },
    fetchPolicy: "no-cache",
  });

  if (response.data.planner?.deleteTemplate?.success) {
    return response.data.planner.deleteTemplate;
  }

  throw new SentryGraphQLError(`api.planner.deletePlannerTemplates`, response);
};

const saveTemplate = async (template: PlannerTemplateInput) => {
  const response = await graphqlClient.query({
    query: SavePlannerTemplateDocument,
    variables: {
      template,
    },
    fetchPolicy: "no-cache",
  });

  if (response.data.planner?.saveTemplate?.success) {
    return response.data.planner.saveTemplate;
  }

  throw new SentryGraphQLError(`api.planner.saveTemplate`, response);
};

export default {
  getFilters,
  getFiltersViaGQL,
  getCategories,
  saveCategory,
  saveFilter,
  deleteFilter,
  getPlannerTemplates,
  deletePlannerTemplates,
  saveTemplate,
};
